import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { signInWithEmailAndPassword, signInWithPopup } from 'firebase/auth';
import { auth, googleProvider } from '../../firebase/Firebase';
import toast from 'react-hot-toast';
import { FaEnvelope ,FaLock,FaGoogle} from 'react-icons/fa';
import axios from 'axios';

interface Login1Props {
  toggleAuth: () => void;
}

interface FormData {
  email: string;
  password: string;
}

const Login1: React.FC<Login1Props> = ({ toggleAuth }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>();

  const onSubmit = async (data: FormData) => {
    setIsLoading(true);
    try {
      const url = process.env.REACT_APP_BACKENDURL;
      
      const userCredential = await signInWithEmailAndPassword(auth, data.email, data.password);
      
      localStorage.setItem("name", userCredential.user.displayName || data.email.split('@')[0]);
      
      try {
        const GetUserNameFromDb = await axios.get(`${url}getUserViaLogin`, {
          params: { email: data.email },
          timeout: 5000 
        });
        
        if (GetUserNameFromDb.data.findUser?.username) {
          localStorage.setItem("name", GetUserNameFromDb.data.findUser.username);
        }
      } catch (apiError) {
        console.warn('Failed to fetch additional user data:', apiError);
      }
      
      toast.success('Login Successfully');
    } catch (error: any) {
      const errorMessage = getErrorMessage(error.code);
      console.log(error);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setIsGoogleLoading(true);
    try {
      const data = await signInWithPopup(auth, googleProvider);
      localStorage.setItem("name", data.user.displayName || "");
      
      toast.success('Logged in with Google');
    } catch (error: any) {
      toast.error('Google Sign-in Failed');
      console.error(error);
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const getErrorMessage = (code: string): string => {
    switch (code) {
      case 'auth/email-already-in-use':
        return 'The email address is already in use. Please use a different email.';
      case 'auth/invalid-email':
        return 'The email address is not valid. Please enter a valid email.';
      case 'auth/weak-password':
        return 'The password is too weak. Please choose a stronger password.';
      case 'auth/user-not-found':
        return 'No account found with this email. Please check your email or create an account.';
      case 'auth/wrong-password':
        return 'Incorrect password. Please try again.';
      default:
        return 'An unexpected error occurred. Please try again.';
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="bg-blue-50 shadow-lg rounded-lg p-8 max-w-md w-full">
        <h2 className="text-3xl font-bold text-center mb-4 text-blue-600">Login to your Account</h2>
        <p className="text-center text-gray-600 mb-6">
          Welcome back! Select method to log in:
        </p>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className='relative'>
          <FaEnvelope className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="email"
              placeholder="Email"
              className={`w-full pl-10 p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.email ? 'border-red-500' : 'border-gray-300'
              }`}
              {...register('email', { required: 'Email is required' })}
              disabled={isLoading}
            />
            {errors.email && (
              <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
            )}
          </div>

          <div className='relative'>
           <FaLock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="password"
              placeholder="Password"
              className={`w-full pl-10 p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.password ? 'border-red-500' : 'border-gray-300'
              }`}
              {...register('password', { required: 'Password is required' })}
              disabled={isLoading}
            />
            {errors.password && (
              <p className="text-red-500 text-sm mt-1">{errors.password.message}</p>
            )}
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className={`w-full p-3 rounded-lg font-semibold transition duration-300 ${
              isLoading 
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {isLoading ? 'Logging in...' : 'LOG IN'}
          </button>

          <div className="flex items-center justify-center my-4">
            <span className="text-gray-500">OR</span>
          </div>

          <button
            type="button"
            disabled={isGoogleLoading}
            className={`w-full p-3 rounded-lg font-semibold transition duration-300 flex items-center justify-center gap-2 ${
              isGoogleLoading 
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-red-500 text-white hover:bg-red-600'
            }`}
            onClick={handleGoogleSignIn}
          >
            <FaGoogle className="mr-2" /> 
            {isGoogleLoading ? 'Signing in...' : 'Sign in with Google'}
          </button>

          <p className="text-center text-gray-600 mt-4">
            Don't have an account?{' '}
            <span
              className="text-blue-600 cursor-pointer hover:underline"
              onClick={toggleAuth}
            >
              Create an account
            </span>
          </p>
        </form>
      </div>
    </div>
  );
};

export default Login1;
